import axios, { AxiosError } from 'axios';
import { Environment } from '../../../environment';
import { Api } from '../axiosApi';

export type TStatusDespesa = 'pendente' | 'pago';

export interface IDespesa {
  iddespesa: string;
  descricao: string;
  categoria: string | null;
  valor: number;
  data: string;
  observacao: string | null;
  idrecorrencia: string | null;
  status: TStatusDespesa;
  datapagamento: string | null;
  formapagamento: string | null;
}

export type IDespesaFormData = Pick<IDespesa, 'descricao' | 'categoria' | 'valor' | 'data' | 'observacao'>;

// Item da listagem: despesa avulsa aparece com `iddespesa` preenchido; lançamento
// recorrente aparece como um resumo (`idrecorrencia` preenchido, `iddespesa` nulo) — só
// ao entrar nele (getParcelas) é que as parcelas individuais aparecem.
export interface IDespesaListagemItem {
  chave: string;
  idrecorrencia: string | null;
  iddespesa: string | null;
  descricao: string;
  categoria: string | null;
  observacao: string | null;
  data: string;
  valor: number;
  quantidadeparcelas: number;
  totalpagas: number;
  valortotal: number;
}

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const tratarErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) {
      return new Error(axiosError.response.data?.errors?.default || 'Erro desconhecido');
    } else if (axiosError.request) {
      return new Error('Nenhuma resposta do servidor');
    }
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

const getAll = async (page = 1, filter = '', limit = Environment.LIMITE_DE_LINHAS): Promise<{ data: IDespesaListagemItem[]; totalCount: number } | Error> => {
  try {
    const { data, headers } = await Api.get('/despesas', { params: { page, limit, filter } });
    return {
      data,
      totalCount: Number(headers['x-total-count'] || 0),
    };
  } catch (error) {
    return tratarErro(error);
  }
};

const getParcelas = async (idrecorrencia: string): Promise<IDespesa[] | Error> => {
  try {
    const { data } = await Api.get(`/despesas/recorrencia/${idrecorrencia}`);
    return data;
  } catch (error) {
    return tratarErro(error);
  }
};

const create = async (dados: IDespesaFormData): Promise<{ iddespesa: string } | Error> => {
  try {
    const { data } = await Api.post('/despesas', dados);
    return data;
  } catch (error) {
    return tratarErro(error);
  }
};

const updateById = async (id: string, dados: IDespesaFormData): Promise<void | Error> => {
  try {
    await Api.put(`/despesas/${id}`, dados);
  } catch (error) {
    return tratarErro(error);
  }
};

const deleteById = async (id: string): Promise<void | Error> => {
  try {
    await Api.delete(`/despesas/${id}`);
  } catch (error) {
    return tratarErro(error);
  }
};

const baixa = async (id: string, datapagamento: string, formapagamento: string): Promise<void | Error> => {
  try {
    await Api.post(`/despesas/${id}/baixa`, { datapagamento, formapagamento });
  } catch (error) {
    return tratarErro(error);
  }
};

const estornarBaixa = async (id: string): Promise<void | Error> => {
  try {
    await Api.post(`/despesas/${id}/estornar`);
  } catch (error) {
    return tratarErro(error);
  }
};

export interface IGerarRecorrenteDespesa {
  descricao: string;
  categoria?: string | null;
  valor: number;
  observacao?: string | null;
  anoinicial: number;
  mesinicial: number;
  quantidademeses: number;
  dialancamento: number;
}

const gerarRecorrente = async (dados: IGerarRecorrenteDespesa): Promise<void | Error> => {
  try {
    await Api.post('/despesas/gerar', dados);
  } catch (error) {
    return tratarErro(error);
  }
};

export const DespesaService = {
  getAll,
  getParcelas,
  create,
  updateById,
  deleteById,
  baixa,
  estornarBaixa,
  gerarRecorrente,
};
